import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BookOpen, Plus, Edit2, Archive, RotateCcw, X, Save, Search
} from 'lucide-react';
import api from '../services/api';
import './DirectoryManager.css';

/* ──────────────── Конфигурация вкладок (функция, чтобы использовать t()) ──────────────── */
function buildTabs(t) {
  return [
    {
      key: 'departments',
      label: t('admin:directories.tabs.departments'),
      fetch: (p) => api.getDirDepartments(p),
      create: (d) => api.createDirDepartment(d),
      update: (id, d) => api.updateDirDepartment(id, d),
      remove: (id) => api.deleteDirDepartment(id),
      restore: (id) => api.restoreDirDepartment(id),
      columns: [
        { key: 'code', label: t('admin:directories.columns.code'), width: '80px' },
        { key: 'name_ru', label: t('admin:directories.columns.nameRu') },
        { key: 'name_uz', label: t('admin:directories.columns.nameUz') },
        { key: 'head_name', label: t('admin:directories.columns.headName') },
      ],
      formFields: [
        { key: 'code', label: t('admin:directories.formFields.code'), type: 'text', required: true },
        { key: 'name_ru', label: t('admin:directories.formFields.nameRu'), type: 'text', required: true },
        { key: 'name_uz', label: t('admin:directories.formFields.nameUz'), type: 'text', required: true },
        { key: 'description', label: t('admin:directories.formFields.description'), type: 'textarea' },
      ],
    },
    {
      key: 'authorities',
      label: t('admin:directories.tabs.authorities'),
      fetch: (p) => api.getDirAuthorities(p),
      create: (d) => api.createDirAuthority(d),
      update: (id, d) => api.updateDirAuthority(id, d),
      remove: (id) => api.deleteDirAuthority(id),
      restore: (id) => api.restoreDirAuthority(id),
      columns: [
        { key: 'name', label: t('admin:directories.columns.name') },
        { key: 'type_display', label: t('admin:directories.columns.typeDisplay') },
      ],
      formFields: [
        { key: 'name', label: t('admin:directories.formFields.name'), type: 'text', required: true },
        {
          key: 'type', label: t('admin:directories.formFields.type'), type: 'select', required: true,
          options: [
            { value: 'president', label: t('admin:directories.authorityTypes.president') },
            { value: 'presidential_admin', label: t('admin:directories.authorityTypes.presidential_admin') },
            { value: 'cabinet', label: t('admin:directories.authorityTypes.cabinet') },
            { value: 'ministry', label: t('admin:directories.authorityTypes.ministry') },
            { value: 'other', label: t('admin:directories.authorityTypes.other') },
          ],
        },
      ],
    },
    {
      key: 'document-types',
      label: t('admin:directories.tabs.documentTypes'),
      fetch: (p) => api.getDirDocumentTypes(p),
      create: (d) => api.createDirDocumentType(d),
      update: (id, d) => api.updateDirDocumentType(id, d),
      remove: (id) => api.deleteDirDocumentType(id),
      restore: (id) => api.restoreDirDocumentType(id),
      columns: [
        { key: 'name', label: t('admin:directories.columns.name') },
        { key: 'description', label: t('admin:directories.columns.description') },
        { key: 'documents_count', label: t('admin:directories.columns.documentsCount'), width: '100px' },
      ],
      formFields: [
        { key: 'name', label: t('admin:directories.formFields.name'), type: 'text', required: true },
        { key: 'description', label: t('admin:directories.formFields.description'), type: 'textarea' },
      ],
    },
    {
      key: 'document-statuses',
      label: t('admin:directories.tabs.documentStatuses'),
      fetch: (p) => api.getDirDocumentStatuses(p),
      create: (d) => api.createDirDocumentStatus(d),
      update: (id, d) => api.updateDirDocumentStatus(id, d),
      remove: (id) => api.deleteDirDocumentStatus(id),
      restore: (id) => api.restoreDirDocumentStatus(id),
      columns: [
        { key: 'name', label: t('admin:directories.columns.name') },
        { key: 'code', label: t('admin:directories.columns.code'), width: '100px' },
        { key: 'color', label: t('admin:directories.columns.color'), width: '70px', render: (v) => <span className="color-dot" style={{ background: v }} /> },
        { key: 'order', label: t('admin:directories.columns.order'), width: '80px' },
        { key: 'documents_count', label: t('admin:directories.columns.linked'), width: '80px' },
      ],
      formFields: [
        { key: 'name', label: t('admin:directories.formFields.name'), type: 'text', required: true },
        { key: 'code', label: t('admin:directories.formFields.code'), type: 'text', required: true },
        { key: 'color', label: t('admin:directories.formFields.color'), type: 'color' },
        { key: 'order', label: t('admin:directories.formFields.order'), type: 'number' },
      ],
    },
    {
      key: 'priorities',
      label: t('admin:directories.tabs.priorities'),
      fetch: (p) => api.getDirPriorities(p),
      create: (d) => api.createDirPriority(d),
      update: (id, d) => api.updateDirPriority(id, d),
      remove: (id) => api.deleteDirPriority(id),
      restore: (id) => api.restoreDirPriority(id),
      columns: [
        { key: 'name', label: t('admin:directories.columns.name') },
        { key: 'level_display', label: t('admin:directories.columns.levelDisplay') },
        { key: 'color', label: t('admin:directories.columns.color'), width: '70px', render: (v) => <span className="color-dot" style={{ background: v }} /> },
        { key: 'order', label: t('admin:directories.columns.order'), width: '80px' },
      ],
      formFields: [
        { key: 'name', label: t('admin:directories.formFields.name'), type: 'text', required: true },
        {
          key: 'level', label: t('admin:directories.formFields.level'), type: 'select', required: true,
          options: [
            { value: 'low', label: t('admin:directories.priorityLevels.low') },
            { value: 'medium', label: t('admin:directories.priorityLevels.medium') },
            { value: 'high', label: t('admin:directories.priorityLevels.high') },
            { value: 'urgent', label: t('admin:directories.priorityLevels.urgent') },
          ],
        },
        { key: 'color', label: t('admin:directories.formFields.color'), type: 'color' },
        { key: 'order', label: t('admin:directories.formFields.order'), type: 'number' },
      ],
    },
    {
      key: 'task-categories',
      label: t('admin:directories.tabs.taskCategories'),
      fetch: (p) => api.getDirTaskCategories(p),
      create: (d) => api.createDirTaskCategory(d),
      update: (id, d) => api.updateDirTaskCategory(id, d),
      remove: (id) => api.deleteDirTaskCategory(id),
      restore: (id) => api.restoreDirTaskCategory(id),
      columns: [
        { key: 'name', label: t('admin:directories.columns.name') },
        { key: 'description', label: t('admin:directories.columns.description') },
        { key: 'color', label: t('admin:directories.columns.color'), width: '70px', render: (v) => <span className="color-dot" style={{ background: v }} /> },
        { key: 'order', label: t('admin:directories.columns.order'), width: '80px' },
        { key: 'tasks_count', label: t('admin:directories.columns.tasksCount'), width: '70px' },
      ],
      formFields: [
        { key: 'name', label: t('admin:directories.formFields.name'), type: 'text', required: true },
        { key: 'description', label: t('admin:directories.formFields.description'), type: 'textarea' },
        { key: 'color', label: t('admin:directories.formFields.color'), type: 'color' },
        { key: 'order', label: t('admin:directories.formFields.order'), type: 'number' },
      ],
    },
  ];
}

/* ──────────────── Компонент ──────────────── */
function DirectoryManager() {
  const { t } = useTranslation(['admin', 'common']);
  const TABS = useMemo(() => buildTabs(t), [t]);
  const [activeTab, setActiveTab] = useState(TABS[0].key);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  const tab = TABS.find(t => t.key === activeTab);

  const fetchItems = useCallback(async () => {
    if (!tab) return;
    setLoading(true);
    try {
      const params = {};
      if (showArchived) params.show_all = 'true';
      if (search) params.search = search;
      const res = await tab.fetch(params);
      setItems(res.data.results || res.data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [tab, showArchived, search]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSearch('');
    setShowArchived(false);
  };

  const openCreate = () => {
    setEditingItem(null);
    const initial = {};
    tab.formFields.forEach(f => {
      if (f.type === 'color') initial[f.key] = '#3b82f6';
      else if (f.type === 'number') initial[f.key] = 0;
      else if (f.type === 'select' && f.options?.length) initial[f.key] = f.options[0].value;
      else initial[f.key] = '';
    });
    setFormData(initial);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    const data = {};
    tab.formFields.forEach(f => {
      data[f.key] = item[f.key] ?? '';
    });
    setFormData(data);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
    setFormData({});
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingItem) {
        await tab.update(editingItem.id, formData);
      } else {
        await tab.create(formData);
      }
      closeModal();
      fetchItems();
    } catch (err) {
      const detail = err.response?.data;
      if (detail) {
        const msgs = Object.values(detail).flat().join(', ');
        alert(msgs || t('admin:directories.saveError'));
      } else {
        alert(t('admin:directories.saveError'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (item) => {
    if (!window.confirm(t('admin:directories.archiveConfirm', { name: item.name || item.name_ru || item.code }))) return;
    try {
      await tab.remove(item.id);
      fetchItems();
    } catch {
      alert(t('admin:directories.archiveError'));
    }
  };

  const handleRestore = async (item) => {
    try {
      await tab.restore(item.id);
      fetchItems();
    } catch {
      alert(t('admin:directories.restoreError'));
    }
  };

  const renderField = (field) => {
    const value = formData[field.key] ?? '';
    if (field.type === 'textarea') {
      return (
        <textarea
          className="dir-input"
          value={value}
          onChange={e => setFormData({ ...formData, [field.key]: e.target.value })}
          rows={3}
        />
      );
    }
    if (field.type === 'select') {
      return (
        <select
          className="dir-input"
          value={value}
          onChange={e => setFormData({ ...formData, [field.key]: e.target.value })}
        >
          {field.options.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      );
    }
    if (field.type === 'color') {
      return (
        <div className="color-input-row">
          <input
            type="color"
            value={value}
            onChange={e => setFormData({ ...formData, [field.key]: e.target.value })}
          />
          <input
            className="dir-input"
            type="text"
            value={value}
            onChange={e => setFormData({ ...formData, [field.key]: e.target.value })}
            style={{ flex: 1 }}
          />
        </div>
      );
    }
    return (
      <input
        className="dir-input"
        type={field.type || 'text'}
        value={value}
        onChange={e => setFormData({
          ...formData,
          [field.key]: field.type === 'number' ? Number(e.target.value) : e.target.value
        })}
        required={field.required}
      />
    );
  };

  return (
    <div className="directory-page">
      {/* Заголовок */}
      <div className="directory-header">
        <h1><BookOpen size={24} /> {t('admin:directories.title')}</h1>
      </div>

      {/* Вкладки */}
      <div className="directory-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`dir-tab ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => handleTabChange(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Панель инструментов */}
      <div className="directory-toolbar">
        <div className="toolbar-left">
          <button className="btn-dir-create" onClick={openCreate}>
            <Plus size={16} /> {t('admin:directories.addNew')}
          </button>
          <label className="toggle-archived">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={e => setShowArchived(e.target.checked)}
            />
            {t('admin:directories.showArchived')}
          </label>
        </div>
        <div className="toolbar-right">
          <div className="dir-search">
            <Search size={16} />
            <input
              type="text"
              placeholder={t('admin:directories.searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Таблица */}
      <div className="directory-table-card">
        {loading ? (
          <div className="dir-loading">{t('common:table.loading')}</div>
        ) : items.length === 0 ? (
          <div className="dir-empty">{t('admin:directories.noRecords')}</div>
        ) : (
          <table className="dir-table">
            <thead>
              <tr>
                {tab.columns.map(col => (
                  <th key={col.key} style={col.width ? { width: col.width } : {}}>
                    {col.label}
                  </th>
                ))}
                <th style={{ width: '70px' }}>{t('admin:directories.status')}</th>
                <th style={{ width: '120px' }}>{t('common:table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id} className={item.is_active === false ? 'row-archived' : ''}>
                  {tab.columns.map(col => (
                    <td key={col.key}>
                      {col.render ? col.render(item[col.key]) : (item[col.key] ?? '—')}
                    </td>
                  ))}
                  <td>
                    {item.is_active === false ? (
                      <span className="badge-archived">{t('admin:directories.archived')}</span>
                    ) : (
                      <span className="badge-active">{t('admin:directories.active')}</span>
                    )}
                  </td>
                  <td className="actions-cell">
                    <button
                      className="btn-icon btn-edit"
                      title={t('admin:directories.editItem')}
                      onClick={() => openEdit(item)}
                    >
                      <Edit2 size={15} />
                    </button>
                    {item.is_active === false ? (
                      <button
                        className="btn-icon btn-restore"
                        title={t('admin:directories.restore')}
                        onClick={() => handleRestore(item)}
                      >
                        <RotateCcw size={15} />
                      </button>
                    ) : (
                      <button
                        className="btn-icon btn-archive"
                        title={t('admin:directories.archive')}
                        onClick={() => handleArchive(item)}
                      >
                        <Archive size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Модальное окно */}
      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingItem ? t('admin:directories.editItem') : t('admin:directories.newItem')}</h2>
              <button className="btn-icon" onClick={closeModal}><X size={20} /></button>
            </div>
            <div className="modal-body">
              {tab.formFields.map(field => (
                <div className="form-group" key={field.key}>
                  <label>
                    {field.label}
                    {field.required && <span className="required">*</span>}
                  </label>
                  {renderField(field)}
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeModal}>{t('common:buttons.cancel')}</button>
              <button className="btn-save" onClick={handleSave} disabled={saving}>
                <Save size={16} />
                {saving ? t('admin:settings.saving') : t('common:buttons.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DirectoryManager;
